import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { Button, Form, Modal, Spin } from "antd"
import { useWatch } from "antd/es/form/Form"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useMatch, useParams, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { getBooking, cleanedBooking, cleanClientId, cleanedExistingClientList, setClientId, refreshClientBookingDetails } from "store/bookings"
import { fetchCommunicationNotificationStatus } from "store/communication"
import BookingForm from "../BookingForm"
import { resetExperienceBookingData, resetExperienceById, fetchExperiences } from "store/experiences"
import { fetchReservationDetail } from "store/reservations"
import { validateDiscount } from "store/discounts"
import { selectedPaymentMethodIdSelector, setSelectedPaymentMethodId, cleanedPaymentMethods } from "store/user"
import { useStripe } from "@stripe/react-stripe-js"
import { submitTripBooking, submitGuideOrder, populateFormWithBooking, hasNoUserBookingChanges } from "../bookingUtils"
import { useDevice } from "hooks/useDevice"
import { useStripeIssue } from "hooks/useStripeIssue"
import { toaster } from "components/common/Toast"
import { numberToCurrency } from "helpers/string"
import { ResponsivePage } from "ui/ResponsivePage"
import { routes } from "router"
import DiscardConfirm from "ui/DiscardModal"
import EditBookingForm from "../BookingForm/EditBookingForm"
import { BOOKING_CONFIRMATION_WIDTH, BOOKING_WINDOW_WIDTH } from "../constants"
import BookingSuccessModal from "ui/BookingSuccessModal"
import { getBookingId } from "pagesV2/BookingWidget/helpers"
import { isMultiDayTrip } from "utils/multiDayPricing"
import { Icon } from "ui/Icon"
import styles from "./BookingWindow.module.css"

const GuideBookingWindow = () => {
  const [form] = Form.useForm()
  const { id: bookingUuid } = useParams()
  const location = useLocation()
  const client = location?.state?.client
  const filterExperienceIds = location?.state?.filterExperienceIds // From equipment details page
  const { isMobile, isTablet } = useDevice()
  const stripe = useStripe()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isStripeChargeable, stripeProblemMessage } = useStripeIssue()
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const isDirtyRef = useRef(false)
  const [appliedPromos, setAppliedPromos] = useState([])
  const [isOriginalDiscountRemoved, setIsOriginalDiscountRemoved] = useState(false)
  const [bookingSelections, setBookingSelections] = useState({
    date: null, timeSlot: null, tripLength: null, counts: null,
    amount: 0, baseAmount: 0, breakdown: [], addOns: [], addOnsTotal: 0
  })
  const updateBookingSelections = useCallback(
    (updates) => setBookingSelections((prev) => ({ ...prev, ...updates })),
    []
  )
  const handlePromoApplied = (promo) => setAppliedPromos((prev) => [...prev, promo])
  const handlePromoRemoved = (code) => setAppliedPromos((prev) => prev.filter((p) => p.code !== code))
  const handleClearPromos = () => setAppliedPromos([])

  const isBookAgain = useMatch(routes.guideBookingBookAgainPath())
  const isEdit = useMatch(routes.guideBookingEditPath())
  const [bookingModalVisible, setBookingModalVisible] = useState(true)
  // The save's confirmation modal while it shows: the booking's ID ("" when the response has none) and
  // whether the save updated an existing booking. Null otherwise. Closing it closes the window.
  const [confirmation, setConfirmation] = useState(null)
  const [formLoading, setFormLoading] = useState(!!bookingUuid)
  const [retryBookingUuid, setRetryBookingUuid] = useState(null)
  // An order whose card payment failed: submitting again pays it rather than booking it twice.
  const [retryOrderUuid, setRetryOrderUuid] = useState(null)
  const { booking } = useSelector((store) => store.bookings)
  const clientId = useSelector((state) => state.bookings.clientId)
  const selectedPaymentMethodId = useSelector(selectedPaymentMethodIdSelector)
  const { experienceDetail, loading: experienceDetailLoading } = useSelector((state) => state.experiences.experienceById)

  // DS-4473. Recovering a booking request that expired before it could be accepted. The edit
  // screen is reused as-is; what changes is that the save also accepts the reservation, and the
  // fields that aren't driven by the new date/time render read-only.
  //
  // recovery_allowed lives on the reservation detail endpoint, not on the /trip_bookings payload
  // this screen loads, so it's fetched on demand — only for an expired request, leaving every
  // ordinary edit with the same number of calls it makes today.
  const isExpiredRequest = Boolean(isEdit) && !isBookAgain && booking?.status === "requested" && Boolean(booking?.is_expired)
  // DS-4473. The other restricted mode: a completed booking opens this same modal to take charges
  // discovered after the trip, and to correct the experience or slot it was booked against. The
  // guest count and check-in state stay frozen, so the booking stays Completed with an outstanding
  // balance.
  //
  // "Completed" is not the only shape of a trip that already happened: a booking stays `booked`
  // after its start time until the nightly sweep completes it, and a slot earlier today never
  // counts as a past date. The backend treats both as post-trip (completed OR is_expired), so the
  // modal must too — otherwise they open as an ordinary edit and a date/duration change clears the
  // guest counts. Which of them may actually be adjusted is still the backend's call, below.
  const isPastBooking =
    Boolean(isEdit) &&
    !isBookAgain &&
    (booking?.status === "completed" || (booking?.status === "booked" && Boolean(booking?.is_expired)))
  const { data: reservationDetail } = useSelector((state) => state.reservations.detail)
  // Both eligibility flags live on the reservation detail endpoint, not on the /trip_bookings
  // payload this screen loads — fetched only for the two cases that need them, so an ordinary edit
  // makes the same calls it makes today.
  useEffect(() => {
    if ((isExpiredRequest || isPastBooking) && bookingUuid) dispatch(fetchReservationDetail(bookingUuid))
  }, [isExpiredRequest, isPastBooking, bookingUuid, dispatch])
  const isDetailForThisBooking = reservationDetail?.uuid === booking?.uuid
  // An expired request outside its 24h window must not open in recovery mode — the backend would
  // refuse the save anyway, and offering the flow implies it will work.
  const isRecovery = isExpiredRequest && isDetailForThisBooking && Boolean(reservationDetail?.canRecover)
  const isPostTrip = isPastBooking && isDetailForThisBooking && Boolean(reservationDetail?.canAdjustPostTrip)
  // While editing, keep the modal overlay spinning until the experience detail API resolves so the
  // duration/time fields don't flash before the experience type (single_day/multi_day) is known.
  const editDetailLoading = !!isEdit && !isBookAgain && (experienceDetailLoading || !experienceDetail)

  // The experience is editable while editing. When it's swapped away from the booking's original
  // trip the add-ons no longer apply (ExperienceDetail clears them on change), but the coupons are
  // re-checked against the new trip rather than dropped outright: on save the backend keeps a
  // coupon that is still valid for the new experience, so dropping it here would show the guide no
  // discount on a booking that saves with one.
  // The experience Select is labelInValue over the trip list, so `key` is the trip's numeric id —
  // the same id a discount's applicable_experiences is expressed in.
  //
  // `preserve: true` is load-bearing, not tidiness. The Select lives in ExperienceDetail, which is
  // rendered on step 1 only; a plain useWatch reads getFieldsValue() — registered fields alone — so
  // the moment the guide moves to step 2 it returns undefined, isExperienceChanged flips back to
  // false, and the booking's old coupon reappears on exactly the screen the guide is looking at.
  // With preserve it reads getFieldsValue(true) and the swap stays visible across both steps.
  const selectedExperience = useWatch("experience", { form, preserve: true })
  const selectedExperienceUuid = selectedExperience?.value
  // Normalized: the field is seeded from the booking with a numeric id but comes back from the
  // Select as the Option's React key, i.e. a string — comparing the two raw would read as a change.
  const selectedExperienceId =
    selectedExperience?.key != null && Number.isFinite(Number(selectedExperience.key)) ? Number(selectedExperience.key) : null
  const isExperienceChanged =
    !!isEdit && !isBookAgain && !!booking?.trip?.uuid && !!selectedExperienceUuid && selectedExperienceUuid !== booking.trip.uuid

  // The booking's own coupon is checked locally and never through /discounts/validate: that
  // endpoint counts this booking's existing use of the code, so a coupon limited to one use per
  // client — or one that has since hit its total usage limit — would wrongly come back invalid.
  const isOriginalPromoValidForExperience = useMemo(() => {
    if (!isExperienceChanged) return true
    const applicableExperiences = booking?.discount_details?.applicable_experiences
    if (applicableExperiences === "all") return true
    if (!Array.isArray(applicableExperiences) || selectedExperienceId == null) return false
    return applicableExperiences.map(Number).includes(Number(selectedExperienceId))
  }, [isExperienceChanged, booking?.discount_details?.applicable_experiences, selectedExperienceId])

  // The guide touched the coupons in this session — removed the booking's own, or typed a new one.
  // `appliedPromos` here is the raw state (only codes entered during this edit), so this is false
  // on load even though the booking already carries a discount.
  const isPromoChanged = !!isEdit && (isOriginalDiscountRemoved || appliedPromos.length > 0)

  const originalPromo = useMemo(() => {
    if (!isEdit) return null
    if (isExperienceChanged && !isOriginalPromoValidForExperience) return null
    // The discount RULE comes from booking.discount_details (booking detail API). Fall back to
    // payment_summary for older shapes.
    const detail = booking?.discount_details
    const paymentSummary = booking?.payment_summary ?? {}
    const code = detail?.code ?? paymentSummary?.discount_code
    if (!code) return null
    const value = detail?.value ?? paymentSummary?.discount_value
    const discountType = detail?.discount_type ?? paymentSummary?.discount_type
    const applyToAddOns =
      detail?.applicability_rules?.apply_to_add_ons ??
      paymentSummary?.applicability_rules?.apply_to_add_ons ??
      paymentSummary?.apply_to_add_ons ??
      false
    const label =
      discountType === "percentage"
        ? `${value}% off`
        : `$${Number(value ?? paymentSummary?.discount_amount ?? 0).toFixed(2)} off`
    const base = { type: "discount", code, name: detail?.name || code, label }

    // The saved discount amount was computed against the old trip's price, so on an experience
    // change it says nothing about the new one: carry the rule, or drop the coupon when there is
    // no rule to recompute from.
    if (isExperienceChanged) {
      if (value == null || !discountType) return null
      return { ...base, value: Number(value), discount_type: discountType, apply_to_add_ons: applyToAddOns }
    }

    // Until the user changes guests/add-ons, show the discount the backend actually applied
    // (payment_summary.discount_amount) so it matches the saved total exactly — recomputing it on
    // the frontend can drift. Once something changes, carry the RULE so the discount recomputes
    // against the new base (guest subtotal + add-ons, per apply_to_add_ons).
    const ready = !!bookingSelections?.counts
    const unchanged = !ready || hasNoUserBookingChanges(bookingSelections, booking, isExperienceChanged, isPromoChanged)
    if (unchanged || value == null || !discountType) {
      return { ...base, discountAmount: Math.max(0, Number(paymentSummary?.discount_amount || 0)) }
    }
    return { ...base, value: Number(value), discount_type: discountType, apply_to_add_ons: applyToAddOns }
  }, [isEdit, isExperienceChanged, isOriginalPromoValidForExperience, isPromoChanged, booking, bookingSelections])

  const effectiveAppliedPromos = useMemo(() => {
    if (!isEdit || isOriginalDiscountRemoved || !originalPromo) return appliedPromos
    return [originalPromo, ...appliedPromos]
  }, [isEdit, isOriginalDiscountRemoved, originalPromo, appliedPromos])

  // A coupon that doesn't carry over is dropped silently: per QA, changing the experience already
  // visibly resets the price, so a toast on top of it is noise.
  //
  // Codes the guide typed in during this edit are re-checked against the new experience on every
  // swap — including switching back to the original trip. The check is deferred until the new trip
  // has a price: /discounts/validate enforces minimum-purchase rules, and ExperienceDetail resets
  // the selections to zero the moment the experience changes.
  const appliedPromosRef = useRef(appliedPromos)
  const bookingSelectionsRef = useRef(bookingSelections)
  useEffect(() => {
    appliedPromosRef.current = appliedPromos
    bookingSelectionsRef.current = bookingSelections
  })

  const [promoRecheckExperienceId, setPromoRecheckExperienceId] = useState(null)
  const promosCheckedForExperienceIdRef = useRef(null)
  useEffect(() => {
    if (!isEdit || isBookAgain || selectedExperienceId == null) return
    // First settle of the watched field is the booking's own experience — nothing has changed yet.
    if (promosCheckedForExperienceIdRef.current === null) {
      promosCheckedForExperienceIdRef.current = selectedExperienceId
      return
    }
    if (promosCheckedForExperienceIdRef.current === selectedExperienceId) return
    promosCheckedForExperienceIdRef.current = selectedExperienceId
    setPromoRecheckExperienceId(selectedExperienceId)
  }, [selectedExperienceId, isEdit, isBookAgain])

  const isPricedForPromoRecheck = (bookingSelections?.baseAmount ?? 0) > 0
  useEffect(() => {
    if (promoRecheckExperienceId == null) return
    if (appliedPromosRef.current.length === 0) {
      setPromoRecheckExperienceId(null)
      return
    }
    if (!isPricedForPromoRecheck) return

    let isStale = false
    const recheckPromos = async () => {
      const selections = bookingSelectionsRef.current
      const clientIdentifier =
        form.getFieldValue("email") ||
        form.getFieldValue("phone_number")?.fullNumber ||
        booking?.client?.email ||
        booking?.client?.phone_number
      const results = await Promise.all(
        appliedPromosRef.current.map(async (promo) => {
          try {
            await dispatch(
              validateDiscount(
                promo.code,
                selections?.baseAmount ?? 0,
                promoRecheckExperienceId,
                clientIdentifier,
                selections?.addOnsTotal ?? 0
              )
            )
            return { code: promo.code, isValid: true }
          } catch {
            return { code: promo.code, isValid: false }
          }
        })
      )
      if (isStale) return
      setPromoRecheckExperienceId(null)
      const invalidCodes = results.filter((result) => !result.isValid).map((result) => result.code)
      if (invalidCodes.length === 0) return
      setAppliedPromos((prev) => prev.filter((promo) => !invalidCodes.includes(promo.code)))
    }

    recheckPromos()
    return () => {
      isStale = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoRecheckExperienceId, isPricedForPromoRecheck])

  const handleEditPromoRemoved = (code) => {
    if (isEdit && code === booking?.payment_summary?.discount_code && !isOriginalDiscountRemoved) {
      setIsOriginalDiscountRemoved(true)
    } else {
      handlePromoRemoved(code)
    }
  }

  const handleCancel = () => {
    if (isDirtyRef.current) {
      setShowDiscardConfirm(true)
    } else {
      closeForm()
    }
  }

  const closeForm = () => {
    isDirtyRef.current = false
    form.resetFields()
    setBookingSelections({ date: null, timeSlot: null, tripLength: null, counts: null, amount: 0, baseAmount: 0, breakdown: [], addOns: [], addOnsTotal: 0 })
    dispatch(cleanClientId())
    dispatch(cleanedPaymentMethods())
    dispatch(setSelectedPaymentMethodId(""))
    dispatch(resetExperienceBookingData())
    dispatch(resetExperienceById())
    dispatch(cleanedExistingClientList())
    dispatch(cleanedBooking())
    setBookingModalVisible(false)
    navigate(-1)
  }

  const handleDiscardCancel = () => {
    setShowDiscardConfirm(false)
  }

  const handleDiscardConfirm = () => {
    setShowDiscardConfirm(false)
    closeForm()
  }

  // Several experiences: one order (New Booking passes its experiences and total). Resolves with
  // { refusedItem } when the API refused one of the experiences ({ key, message }), else nothing.
  const handleOrderSubmit = async ({ items, total }) => {
    const paymentType = form.getFieldValue("paymentType")
    if ((paymentType === "card" || paymentType === "link") && Number(total) > 0 && !isStripeChargeable) {
      toaster.error(stripeProblemMessage || t("form_validation.payment_provider_missing"))
      return null
    }
    // The order exists and asked for a card: only another card attempt can pay it here.
    if (retryOrderUuid && paymentType !== "card") {
      toaster.error(t("booking.added_experiences.order_pending_card_only"))
      return null
    }
    setFormLoading(true)
    try {
      const order = await submitGuideOrder({
        form,
        dispatch,
        stripe,
        items,
        orderTotal: total,
        selectedPaymentMethodId,
        clientId,
        client,
        retryOrderUuid
      })
      setRetryOrderUuid(null)
      setBookingModalVisible(false)
      setConfirmation({ bookingId: order?.order_number || "", isUpdate: false })
    } catch (err) {
      if (err?.retryOrderUuid) setRetryOrderUuid(err.retryOrderUuid)
      setFormLoading(false)
      return { refusedItem: err?.refusedItem ?? null }
    }
    setFormLoading(false)
    return null
  }

  const handleSubmit = async () => {
    // Defense in depth: the Stripe payment options are disabled in the UI when Stripe can't charge,
    // but that only removes the affordance. Refuse to submit a Stripe charge here too, so re-enabling
    // the option via dev tools can't push a payment through. (The backend remains the authoritative guard.)
    const paymentType = form.getFieldValue("paymentType")
    const usesStripe = paymentType === "card" || paymentType === "link"
    // A new booking discounted to nothing places no charge at all, so a leftover "card" selection
    // from before the coupon was applied must not make a broken Stripe connection block the save.
    const hasNothingToCollect = !isEdit && Number(bookingSelections?.amount ?? 0) <= 0
    const willCollectPayment = !hasNothingToCollect && (!isEdit || form.getFieldValue("collectPayment") === "yes")
    if (usesStripe && willCollectPayment && !isStripeChargeable) {
      toaster.error(stripeProblemMessage || t("form_validation.payment_provider_missing"))
      return
    }

    setFormLoading(true)
    try {
      const result = await submitTripBooking({
        form,
        dispatch,
        stripe,
        bookingSelections,
        isMultiDay: isMultiDayTrip(experienceDetail),
        selectedPaymentMethodId,
        bookingUuid: retryBookingUuid || bookingUuid,
        isBookAgain,
        isEdit,
        clientId,
        client,
        booking,
        appliedPromos,
        isOriginalDiscountRemoved,
        isRecovery,
        isPostTrip
      })

      // Recovery can succeed with the money still outstanding — the slot is committed atomically,
      // the charge isn't. Say which happened instead of a blanket success, so nobody reads a
      // declined card as collected.
      if (isRecovery) {
        const outstanding = Number(result?.payment_summary?.remaining_balance ?? result?.data?.payment_summary?.remaining_balance ?? 0)
        if (outstanding > 0) {
          toaster.success(t("booking.recovery.success_balance_due", { amount: numberToCurrency(outstanding) }))
        } else {
          toaster.success(t("booking.recovery.success"))
        }
      }

      // Clear retryBookingUuid only if booking is successful
      setRetryBookingUuid(null)

      // The details view we return to reads `clientBookingDetails` (the /booking_detail
      // endpoint), which the update did not refresh. Re-fetch it — with retry, since that
      // endpoint can briefly return the pre-update snapshot — so it shows the latest data
      // instead of staying stale until a manual refresh. Awaited so the slice is fresh before
      // we navigate back. (Skip for book-again, which creates a separate new booking.)
      if (isEdit && !isBookAgain && bookingUuid) {
        await dispatch(refreshClientBookingDetails(bookingUuid))
      }

      // A new booking, or Book Again (which creates one), is confirmed (Figma 7378:129723); an edit is
      // confirmed as updated. Recovery has already toasted whether a balance is still due.
      if (isRecovery) {
        closeForm()
      } else {
        setBookingModalVisible(false)
        setConfirmation({ bookingId: getBookingId(result?.data ?? result) || getBookingId(booking) || "", isUpdate: !!isEdit })
      }
    } catch (err) {
      if (err?.retryBookingUuid) {
        setRetryBookingUuid(err?.retryBookingUuid)
      } else {
        console.error("Booking failed:", err)
      }
    }

    setFormLoading(false)
  }

  useEffect(() => {
    return () => {
      dispatch(cleanClientId())
      dispatch(cleanedPaymentMethods())
      dispatch(setSelectedPaymentMethodId(""))
      dispatch(resetExperienceBookingData())
      dispatch(resetExperienceById())
      dispatch(cleanedExistingClientList())
      dispatch(cleanedBooking())
    }
  }, [dispatch])

  // Fetch the experience list for the experience dropdown. Needed for new bookings, book-again, and
  // now edit too — the experience is editable while editing an existing booking, so the full list
  // must be available to switch to.
  useEffect(() => {
    dispatch(fetchExperiences("booking_window"))
  }, [dispatch])

  useEffect(() => {
    const fetchAndPopulate = async () => {
      if (!bookingUuid) return
      dispatch(cleanedBooking())
      dispatch(resetExperienceById())
      dispatch(resetExperienceBookingData())
      form.resetFields()
      setFormLoading(true)

      try {
        const [res, notificationRes] = await Promise.all([
          dispatch(getBooking(bookingUuid, true, isBookAgain)),
          // dispatch(fetchCommunicationNotificationStatus())
        ])
        const bookingData = res?.payload || res

        if (bookingData) {
          const initialSelections = await populateFormWithBooking({ booking: bookingData, bookingUuid, dispatch, form, isBookAgain })
          if (initialSelections) setBookingSelections(initialSelections)
          if (!isBookAgain) {
            const isLocked = bookingData?.booking_source === "marketplace" || bookingData?.booking_source === "website"
            const notifyDefault = isLocked ? true : (notificationRes?.data?.edit_booking_email_allowed ?? true)
            form.setFieldsValue({ client_notify: notifyDefault, send_guest_email: notifyDefault })
          }
          isDirtyRef.current = false
        } else {
          console.warn("No booking returned to populate")
        }
      } catch (err) {
        console.error("Error in fetchAndPopulate", err)
      } finally {
        setFormLoading(false)
      }
    }

    fetchAndPopulate()
  }, [bookingUuid, isBookAgain, dispatch, form])

  useEffect(() => {
    if (client?.user_id) {
      form.setFieldsValue({
        firstName: client?.user?.first_name,
        lastName: client?.user?.last_name,
        email: client?.user?.email,
        phone_number: { fullNumber: client?.user?.phone_number, countryCode: client?.user?.country_code }
      })
      dispatch(setClientId(client?.user_id))
    }
  }, [dispatch, form, client])

  const handleValuesChange = () => {
    isDirtyRef.current = true
  }
  const title = isEdit && !isBookAgain ? t("bookings.edit") : t("bookings.add")
  // While New Booking's Add / Edit Experience view is open, the header is only "< Back", which leaves
  // the view (Figma 5795:175252); the window's own X comes back with the form.
  const [experienceViewBack, setExperienceViewBack] = useState(null)
  const handleExperienceViewChange = useCallback((back) => setExperienceViewBack(() => back), [])

  return (
    <>
      {isMobile ? (
        <ResponsivePage onBackClick={experienceViewBack || handleCancel} pageTitle={title}>
          <Spin spinning={formLoading || editDetailLoading}>
            {isEdit ? (
              <EditBookingForm
                form={form}
                bookingUuid={bookingUuid}
                isEdit={isEdit}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onCancelModal={handleCancel}
                bookingModalVisible={bookingModalVisible}
                onValuesChange={handleValuesChange}
                appliedPromos={effectiveAppliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handleEditPromoRemoved}
                isExperienceChanged={isExperienceChanged}
                isOriginalDiscountRemoved={isOriginalDiscountRemoved}
                isPromoChanged={isPromoChanged}
                bookingSelections={bookingSelections}
                updateBookingSelections={updateBookingSelections}
                isRecovery={isRecovery}
                isPostTrip={isPostTrip}
              />
            ) : (
              <BookingForm
                form={form}
                bookingUuid={bookingUuid}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onOrderSubmit={handleOrderSubmit}
                isOrderPending={!!retryOrderUuid}
                bookingModalVisible={bookingModalVisible}
                filterExperienceIds={filterExperienceIds}
                appliedPromos={appliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handlePromoRemoved}
                onClearPromos={handleClearPromos}
                onValuesChange={handleValuesChange}
                bookingSelections={bookingSelections}
                updateBookingSelections={updateBookingSelections}
                onExperienceViewChange={handleExperienceViewChange}
              />
            )}
          </Spin>
        </ResponsivePage>
      ) : (
        <Modal
          // New Booking fills a tablet's screen (Figma 5846:125628); Edit Booking stays a window.
          width={isTablet && !isEdit ? "100vw" : BOOKING_WINDOW_WIDTH}
          className={isTablet && !isEdit ? styles.tabletWindow : undefined}
          title={
            experienceViewBack ? (
              <Button
                type="link"
                className={styles.backLink}
                icon={<Icon type="feather" name="ChevronLeft" width={20} height={20} color="currentColor" />}
                onClick={experienceViewBack}
              >
                {t("global.back")}
              </Button>
            ) : (
              title
            )
          }
          open={bookingModalVisible}
          onCancel={handleCancel}
          footer={null}
          maskClosable={false}
          closable={!experienceViewBack}
        >
          <Spin spinning={formLoading || editDetailLoading}>
            {isEdit ? (
              <EditBookingForm
                form={form}
                bookingUuid={bookingUuid}
                isEdit={isEdit}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onCancelModal={handleCancel}
                bookingModalVisible={bookingModalVisible}
                onValuesChange={handleValuesChange}
                appliedPromos={effectiveAppliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handleEditPromoRemoved}
                isExperienceChanged={isExperienceChanged}
                isOriginalDiscountRemoved={isOriginalDiscountRemoved}
                isPromoChanged={isPromoChanged}
                bookingSelections={bookingSelections}
                updateBookingSelections={updateBookingSelections}
                isRecovery={isRecovery}
                isPostTrip={isPostTrip}
              />
            ) : (
              <BookingForm
                form={form}
                bookingUuid={bookingUuid}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onOrderSubmit={handleOrderSubmit}
                isOrderPending={!!retryOrderUuid}
                bookingModalVisible={bookingModalVisible}
                filterExperienceIds={filterExperienceIds}
                appliedPromos={appliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handlePromoRemoved}
                onClearPromos={handleClearPromos}
                onValuesChange={handleValuesChange}
                bookingSelections={bookingSelections}
                updateBookingSelections={updateBookingSelections}
                onExperienceViewChange={handleExperienceViewChange}
              />
            )}
          </Spin>
        </Modal>
      )}

      <BookingSuccessModal
        isOpen={!!confirmation}
        width={BOOKING_CONFIRMATION_WIDTH}
        closeIcon
        title={t(confirmation?.isUpdate ? "booking.confirmation.updated_title" : "booking.confirmation.title")}
        idText={confirmation?.bookingId ? t("booking.confirmation.booking_id", { id: confirmation.bookingId }) : null}
        onClose={closeForm}
      />

      {/* Discard Confirmation Modal */}
      <DiscardConfirm
        open={showDiscardConfirm}
        onCancel={handleDiscardCancel}
        onConfirm={handleDiscardConfirm}
        title={t("Discard Changes?")}
        description={t("global.discard_changes")}
        t={t}
      />
    </>
  )
}

export default GuideBookingWindow
